import { useMutation } from "@tanstack/react-query";

import { ApiError } from "@config/apiClient";
import { queryClient } from "@config/queryClient";
import { useBugsnagErrorHandler } from "@hooks/useBugsnagErrorHandler";
import { DisconnectedError } from "@app-types/errors";
import usePlatformStore from "@store/usePlatformStore";
import noteService from "@services/note.service";
import ToastService from "@services/toast.service";
import { ERROR_STRINGS, ERROR_MESSAGES } from "@config/messages";
import { RelationObjectType } from "@enums/relations.enum";
import { AINote } from "@app-types/notes.type";
import {
  BOUND_NOTE_QUERY_KEY,
  MEETING_QUERY_KEY,
  OBJECT_TAGS_QUERY_KEY,
  SUGGESTED_TAGGING_OPTIONS_QUERY_KEY,
  UPDATE_NOTE_MUTATION_KEY,
} from "@config/query";

export function useUpdateNote(objectId: string, contentType: RelationObjectType) {
  const { notifyBugsnag } = useBugsnagErrorHandler();
  const { itemId } = usePlatformStore();

  return useMutation({
    mutationKey: [UPDATE_NOTE_MUTATION_KEY],
    mutationFn: async (request: { noteId: string; data: Partial<AINote> }) => {
      return await noteService.updateNote(request.noteId, request.data);
    },
    onSuccess: (response) => {
      queryClient.setQueryData([BOUND_NOTE_QUERY_KEY, objectId], response);
      void queryClient.invalidateQueries({ queryKey: [MEETING_QUERY_KEY, itemId] });
      void queryClient.invalidateQueries({
        queryKey: [OBJECT_TAGS_QUERY_KEY, contentType, objectId],
      });
      void queryClient.invalidateQueries({
        queryKey: [SUGGESTED_TAGGING_OPTIONS_QUERY_KEY, contentType, objectId],
      });
    },
    onError: (error) => {
      if (error instanceof DisconnectedError) return;
      notifyBugsnag(error, ERROR_STRINGS.SAVE_NOTE);
      ToastService.showError(error as ApiError, ERROR_MESSAGES.SAVE_NOTE);
    },
  });
}
